import React, { useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useApolloClient } from "@apollo/client";
import getMyAccess from "../graphql/queries/getMyAccess";
import { FeatureAccessProvider } from "./FeatureAccessContext";

export function createAccessLoader(client, { timeoutMs = 15000 } = {}) {
  // identity is provider-side response binding, never a GraphQL variable.
  return async ({ signal }) => {
    if (signal?.aborted) {
      throw new Error("Feature access request was interrupted");
    }
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    let timer;
    const stop = () => controller.abort();
    const interrupted = new Promise((_, reject) => {
      controller.signal.addEventListener("abort", () => reject(new Error("Feature access request was interrupted")), { once: true });
      timer = setTimeout(stop, timeoutMs);
    });
    try {
      const result = await Promise.race([interrupted, client.query({
        query: getMyAccess,
        fetchPolicy: "no-cache",
        errorPolicy: "none",
        context: {
          queryDeduplication: false,
          fetchOptions: { signal: controller.signal },
        },
      })]);
      if (result.errors?.length || !result.data?.getMyAccess || controller.signal.aborted) {
        throw new Error("Feature access could not be verified");
      }
      return result.data.getMyAccess;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    }
  };
}

export default function AuthenticatedFeatureAccess({ children }) {
  const { instance, accounts } = useMsal();
  const client = useApolloClient();
  const account = accounts.length ? instance.getActiveAccount() : null;
  // MSAL metadata binds responses only. The API verifies identity itself.
  // Missing claims stay an invalid descriptor, not an anonymous granted user.
  const identity = account ? {
    tenantId: account.idTokenClaims?.tid,
    objectId: account.idTokenClaims?.oid,
  } : null;
  const loadAccess = useMemo(() => createAccessLoader(client), [client]);
  return <FeatureAccessProvider identity={identity} loadAccess={loadAccess}>{children}</FeatureAccessProvider>;
}